import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon, 
  EyeIcon, 
  PencilIcon, 
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { SeasonService, LeagueSeasonService } from '../../../services/firebaseService';

export default function SeasonList() {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, complete

  useEffect(() => {
    loadSeasons();
  }, []);

  const loadSeasons = async () => {
    try {
      setLoading(true);
      const allSeasons = await SeasonService.getAllSeasons();
      setSeasons(allSeasons);
    } catch (error) {
      console.error('Error loading seasons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (seasonId, newStatus) => {
    try {
      await SeasonService.updateSeason(seasonId, { status: newStatus });
      // Sync league-season isActive: inactive when season is complete, active when reactivated
      await LeagueSeasonService.updateIsActiveForGlobalSeason(
        seasonId,
        newStatus === 'active'
      );
      loadSeasons(); // Reload the list
    } catch (error) {
      console.error('Error updating season status:', error);
    }
  };

  const filteredSeasons = seasons
    .filter(season => {
      if (filter === 'all') return true;
      return season.status === filter;
    })
    .sort((a, b) => {
      // Sort by status: active first, then complete
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (a.status !== 'active' && b.status === 'active') return 1;
      
      // If same status, sort by season name/number (assuming season IDs are like "season-48", "season-49")
      return b.id.localeCompare(a.id);
    });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="h-3 w-3 mr-1" />
            Active
          </span>
        );
      case 'complete':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircleIcon className="h-3 w-3 mr-1" />
            Complete
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Draft
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Manage Seasons</h1>
              <p className="mt-1 text-sm text-gray-500">
                Create and manage Survivor seasons for fantasy leagues.
              </p>
            </div>
            <Link
              to="/admin/seasons/create"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Season
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow rounded-lg p-4">
            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Filter by status:</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
              >
                <option value="all">All Seasons</option>
                <option value="active">Active</option>
                <option value="complete">Complete</option>
              </select>
            </div>
          </div>
        </div>

        {/* Seasons List */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {filteredSeasons.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {filteredSeasons.map((season) => (
                  <li key={season.id}>
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-survivor-100 flex items-center justify-center">
                              <CalendarIcon className="h-6 w-6 text-survivor-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <h3 className="text-lg font-medium text-gray-900">{season.name}</h3>
                              <div className="ml-2">{getStatusBadge(season.status)}</div>
                            </div>
                            <div className="mt-1 flex items-center text-sm text-gray-500">
                              <span>Contestants: {season.contestants?.length || 0}</span>
                              <span className="mx-2">•</span>
                              <span>Weeks: {season.weeks?.length || 0}</span>
                              {season.startDate && (
                                <>
                                  <span className="mx-2">•</span>
                                  <span>Started: {new Date(season.startDate.seconds * 1000).toLocaleDateString()}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/admin/seasons/${season.id}`}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-survivor-700 bg-survivor-100 hover:bg-survivor-200"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            View
                          </Link>
                          <Link
                            to={`/admin/seasons/${season.id}/edit`}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Edit
                          </Link>
                          {season.status === 'active' && (
                            <button
                              onClick={() => handleStatusChange(season.id, 'complete')}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200"
                            >
                              <XCircleIcon className="h-4 w-4 mr-1" />
                              Complete
                            </button>
                          )}
                          {season.status === 'complete' && (
                            <button
                              onClick={() => handleStatusChange(season.id, 'active')}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200"
                            >
                              <CheckCircleIcon className="h-4 w-4 mr-1" />
                              Reactivate
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-12">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No seasons found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filter === 'all' 
                    ? "Get started by creating a new season."
                    : `No ${filter} seasons found.`
                  }
                </p>
                {filter === 'all' && (
                  <div className="mt-6">
                    <Link
                      to="/admin/seasons/create"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      New Season
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
